import { StreamPlayerApi } from "@cloudflare/stream-react";

/**
 * Supported aspect ratios for videos and images.
 */
export enum AspectRatio {
  Landscape = "landscape", // 16:9
  Instagram = "instagram", // 4:5
  Portrait = "portrait", // 9:16
}

/**
 * Common video asset fields.
 */
export interface HasVideoAsset {
  /** Unique identifier for the video from Cloudflare Stream */
  videoId: string;
  /** Timestamp (in seconds) for generating a thumbnail */
  thumbnailTimestampSeconds?: number;
  /** Path to the video transcript */
  transcriptPath?: string;
  /** Aspect ratio for the video asset */
  aspectRatio?: AspectRatio;
}

/**
 * Video metadata and asset.
 */
export interface Video extends HasVideoAsset {
  /** Slug for the video */
  slug?: string;
  /** Title for the video */
  title?: string;
  /** Optional override for SEO or display title */
  pageTitle?: string;
  /** Subtitle for the video */
  subtitle?: string;
  /** Overline for the video */
  overline?: string;
  /** Description of the video */
  description?: string;
  /** URL or path to the thumbnail image */
  thumbnail?: string;
}

/**
 * Workout information.
 */
export interface TwelveWeekWorkout {
  /** Title of the workout */
  title: string;
  /** Exercises included in the workout */
  exercises: TwelveWeekExercise[];
}

/**
 * Program phase information.
 */
export interface TwelveWeekProgramPhase extends HasVideoAsset {
  /** Main heading displayed at the top of the page (e.g., "Weeks 1–4") */
  title: string;
  /** Optional override for SEO or display title */
  pageTitle?: string;
  /** Short subtitle placed below the title, reinforcing the theme or goal of the phase */
  subtitle: string;
  /** Descriptive body copy for the “Training Focus” section—multiple paragraphs allowed */
  description: string;
  /** Schedule type for the program phase */
  scheduleType?: ScheduleType;
  /** Whether the phase includes exercise variations */
  variations?: boolean;
  transcriptPath?: string;
}

export type ProgramNavigationProps = {
  currentSlug: string;
  program: "12-week" | "hypertrophy" | "nutrition" | "exercises";
  nutritionEntries?: Record<string, { title: string }>;
  nutritionIntroHref?: string;
  nutritionEntryHref?: string;
  nutritionIntroLabel?: string;
  fromProgram?: string;
  fromWeek?: string;
};

/**
 * Supported schedule types for program phases.
 */
export enum ScheduleType {
  FourDays = "4-days-a-week",
  SixDays = "6-days-a-week",
}

/**
 * Key moment within an exercise video.
 */
export interface ExerciseKeyMoment {
  /** Label for the key moment */
  label: string;
  /** Timestamp as "m:ss" or seconds (input), or timestampSeconds (output) */
  timestamp?: string | number;
  /** Timestamp (in seconds) for the key moment (output only) */
  timestampSeconds?: number;
}

/**
 * Exercise information.
 */
export interface TwelveWeekExercise extends HasVideoAsset {
  /** Title of the exercise */
  title: string;
  /** Slug for the exercise */
  slug: string;
  /** Weeks to show this exercise on */
  showOnWeeks?: string[];
  /** Description of the exercise */
  description?: string;
  /** Key moments for form points */
  formPoints?: ExerciseKeyMoment[];
  /** Key moments for initial setup */
  initialSetup?: ExerciseKeyMoment[];
  /** Variations for the exercise */
  variations?: string[];
  /** Optional FAQ video for the exercise */
  faqVideo?: Video;
  /** Optional title for the FAQ video */
  faqVideoTitle?: string;
  /** Optional description for the FAQ video */
  faqVideoDescription?: string;
}

/**
 * Route parameters for Next.js pages.
 */
export type RouteParams = { params: { slug: string } };

/**
 * Coach information.
 */
export interface Coach extends HasVideoAsset {
  /** Coach's name */
  name: string;
  /** Coach's role or specialty */
  role: string;
  /** Facebook group URL */
  facebookGroup?: string;
  /** Instagram profile URL */
  instagram?: string;
  /** Facebook profile URL */
  facebook?: string;
  /** YouTube channel URL */
  youtube?: string;
  /** TikTok profile URL */
  tiktok?: string;
  /** Personal website URL */
  website?: string;
  /** Email address */
  email?: string;
  /** URL to coach's image */
  imageUrl: string;
  /** Intrinsic width of the image/video */
  intrinsicWidth?: number;
  /** Intrinsic height of the image/video */
  intrinsicHeight?: number;
  /** Coach's biography */
  bio: string;
  /** Number of Instagram followers */
  instagramFollowers?: number;
  /** Optional call to action text */
  cta?: string;
  /** Optional link for the call to action */
  ctaLink?: string;
}

/**
 * Props for the VideoPlayer component.
 */
export type VideoPlayerProps = {
  video: Video;
  currentTime?: number;
  streamRef?: React.MutableRefObject<StreamPlayerApi | undefined>;
  aspectRatio?: AspectRatio;
  intrinsicWidth?: number;
  intrinsicHeight?: number;
};

/**
 * Supported schedule types for program phases.
 */
export enum HypertrophyWorkouts {
  ChestBack = "chest-back",
  ArmsSmallerMuscles = "arms-smaller-muscles",
  LegsLowerBack = "legs-lower-back",
}

export type HypertrophyExerciseLink = {
  title: string;
  description: string;
  hypertrophyWorkout: HypertrophyWorkouts; // Hypertrophy workout slug, needed for link and videoId thumbnail generation
  thumbnailTimestampSeconds: number;
  startTime: number;
};

export enum ProgramType {
  TwelveWeek = "12-week",
  Hypertrophy = "hypertrophy",
  Nutrition = "nutrition",
}
